import type {
  StoryCurrentMember,
} from "../../components/organisms/ShareStoryModal/ShareStoryModal.types";
import {
  STORY_MEMBER_ROLE_LABELS,
  storyMemberDisplayName,
} from "../dataStories/storyMembersMappers";
import type { StoryCollaboratorRow } from "../dataStories/storyMembers.types";
import type {
  WorkspaceInviteRole,
  WorkspaceMemberInvite,
} from "../workspaces/workspaceMembers.types";
import type { InviteeRequest, StoryInviteeRead, StoryInviteeStatus } from "./storyInvitees.types";
import { toAvatarUrl } from "../account/accountProfileMappers";

/**
 * Rows the "Invite members" list actually renders. `expired`/`revoked`
 * invites are lifecycle/audit states with no UI treatment yet (no status
 * pill, no resend/revoke action) — surfacing them here would read as an
 * active member with no way to explain why. Flagged as a gap, not silently
 * decided: see the ShareStoryModal wiring handoff.
 */
const DISPLAYED_STATUSES: StoryInviteeStatus[] = ["pending", "accepted"];

const isAssignableRole = (
  role: StoryInviteeRead["role"],
): role is WorkspaceInviteRole => role !== "owner";

const joinName = (
  firstName: string | null,
  lastName: string | null,
): string | undefined =>
  [firstName, lastName].filter(Boolean).join(" ").trim() || undefined;

const PENDING_LABEL = "Pending";

/** The one filter every surface listing invitees shares: shown statuses, no owner row. */
const displayedInvitees = (invitees: StoryInviteeRead[]): StoryInviteeRead[] =>
  invitees
    .filter((invitee) => DISPLAYED_STATUSES.includes(invitee.status))
    .filter((invitee) => isAssignableRole(invitee.role));

/**
 * Maps the invite-lifecycle read model to the "current members" rows the
 * modal renders. The story owner is intentionally not derived from here —
 * none of the three invitees/tenant-users endpoints expose who created the
 * story, so `ShareStoryModal`'s owner row stays on its existing mock until a
 * story-detail endpoint carries that field.
 */
export const mapStoryInviteesToCurrentMembers = (
  invitees: StoryInviteeRead[],
): StoryCurrentMember[] =>
  displayedInvitees(invitees).map((invitee) => ({
    id: invitee.user_id ?? invitee.id,
    email: invitee.email ?? invitee.invite_email ?? "",
    name: joinName(invitee.first_name, invitee.last_name),
    designation: null,
    role: invitee.role as WorkspaceInviteRole,
  }));

/**
 * Rows for the story header's collaborators dropdown. Same people, same order
 * as the Share modal's list (both go through `displayedInvitees`), so the two
 * surfaces can never disagree. The API has no display name, so a pending
 * email-only invite falls back to the email local-part — keeps the initials
 * avatar meaningful ("B" for barry@…) instead of "?".
 */
export const mapStoryInviteesToCollaboratorRows = (
  invitees: StoryInviteeRead[],
): StoryCollaboratorRow[] =>
  displayedInvitees(invitees).map((invitee) => {
    const email = invitee.email ?? invitee.invite_email ?? "";
    const picture = invitee.profile_picture?.trim();
    return {
      id: invitee.user_id ?? invitee.id,
      name:
        joinName(invitee.first_name, invitee.last_name) ??
        storyMemberDisplayName({ full_name: null, email }),
      email,
      roleLabel: STORY_MEMBER_ROLE_LABELS[invitee.role],
      statusLabel: invitee.status === "pending" ? PENDING_LABEL : null,
      avatarUrl: toAvatarUrl(picture),
    };
  });

/** Maps a draft invite from the shared picker into one `POST` request entry. */
export const mapMemberInvitesToInviteeRequests = (
  invites: WorkspaceMemberInvite[],
): InviteeRequest[] =>
  invites.map((invite) =>
    invite.kind === "user"
      ? { user_id: invite.userId, role: invite.role }
      : { email: invite.email, role: invite.role },
  );
